// FILE: apps/admin/src/pages/Orders.tsx
// VERSION: 2.1.0
// START_MODULE_CONTRACT
//   PURPOSE: Admin Orders management page with responsive mobile-cards layout, displaying real buyer/recipient identities with avatars, status filtering, search by buyer or recipient, order inspection modal, cancellation, delivery retry, and Step-Up manual payment accreditation («Засчитать оплату»).
//   SCOPE: Orders component and OrderDetail modal.
//   DEPENDS: ../api.js, ../components/ConfirmDialog.js, ../components/Icons.js, ../components/UserAvatar.js, ../display-currency.js, ../toast.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   ORDERS_STATUS_OPTIONS - Supported order status filter options including DELIVERING_ASYNC and REFUNDED
//   Orders - Admin Orders table with buyer/recipient identity pills, provider/status filters, and manual payment accreditation workflow
//   formatProviderBadgeLabel - Format external fulfillment provider code (FRAGMENT, PLAYWALLET, MOOGOLD, GAMESDROP, PIKASIM, LOCAL_STOCK) into operator badge label
// END_MODULE_MAP

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { api, dt, num, rub } from '../api.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { AdminIcon } from '../components/Icons.js';
import { UserAvatar } from '../components/UserAvatar.js';
import { useDisplayCurrency } from '../display-currency.js';
import { useToast } from '../toast.js';

interface OrderRow {
  id: string;
  shortId: string;
  userId?: string;
  status: string;
  product: string;
  starsQty: number;
  premiumMonths: number | null;
  recipientType: string;
  recipientUsername: string | null;
  amountKopecks: number;
  balanceAppliedKopecks: number;
  paymentRail?: string;
  gramAppliedMilli?: number;
  retailTotalKopecks?: number;
  productTitleSnapshot?: string | null;
  externalProvider?: string;
  externalOrderRef?: string | null;
  nextDeliveryAttemptAt?: string | null;
  buyerTelegramId?: number | null;
  buyerUsername?: string | null;
  buyerDisplayName?: string | null;
  buyerPhotoUrl?: string | null;
  createdAt: string;
  expiresAt: string;
  deliveredAt: string | null;
  failureReason: string | null;
}

export const ORDERS_STATUS_OPTIONS = [
  'ALL',
  'AWAITING_PAYMENT',
  'MATCHING',
  'MATCHED',
  'DELIVERING',
  'DELIVERING_ASYNC',
  'DELIVERED',
  'REFUNDED',
  'DELIVERY_FAILED',
  'EXPIRED',
  'CANCELLED',
] as const;

// START_CONTRACT: formatProviderBadgeLabel
//   PURPOSE: Convert externalProvider code into a concise human-readable badge title.
//   INPUTS: provider?: string | null
//   OUTPUTS: string
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: formatProviderBadgeLabel
export function formatProviderBadgeLabel(provider?: string | null): string {
  // START_BLOCK_FORMAT_PROVIDER_BADGE_LABEL
  if (provider === 'PLAYWALLET') return 'Play Wallet';
  if (provider === 'MOOGOLD') return 'MooGold';
  if (provider === 'LOCAL_STOCK') return 'Свой склад';
  if (provider === 'GAMESDROP') return 'GamesDrop';
  if (provider === 'PIKASIM') return 'PikaSim';
  return 'Fragment';
  // END_BLOCK_FORMAT_PROVIDER_BADGE_LABEL
}

function productText(o: OrderRow): string {
  // START_BLOCK_PRODUCT_TEXT
  if (o.product === 'CATALOG') return o.productTitleSnapshot || 'Товар каталога';
  if (o.product === 'PREMIUM') return `Premium ${o.premiumMonths ?? 3} мес.`;
  if (o.product === 'DEPOSIT') return 'Пополнение баланса';
  return `${num(o.starsQty)} Stars`;
  // END_BLOCK_PRODUCT_TEXT
}

function productLabel(o: OrderRow): ReactNode {
  // START_BLOCK_PRODUCT_LABEL
  if (o.product === 'CATALOG') {
    const prov = formatProviderBadgeLabel(o.externalProvider);
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <AdminIcon name={o.externalProvider === 'PIKASIM' ? 'globe' : 'sparkle'} size={15} />
        <span>{o.productTitleSnapshot || 'Товар каталога'}</span>
        <span className="chip info" style={{ padding: '1px 6px', fontSize: 10 }}>
          {prov}
        </span>
      </span>
    );
  }
  if (o.product === 'PREMIUM') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <AdminIcon name="premium" size={15} />
        <span>Premium {o.premiumMonths ?? 3} мес.</span>
      </span>
    );
  }
  if (o.product === 'DEPOSIT') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <AdminIcon name="gram" size={15} />
        <span>Пополнение баланса</span>
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <AdminIcon name="star" size={15} />
      <span>{num(o.starsQty)} Stars</span>
    </span>
  );
  // END_BLOCK_PRODUCT_LABEL
}

function resolveBuyerPrimaryTitle(o: OrderRow): string {
  // START_BLOCK_RESOLVE_BUYER_PRIMARY_TITLE
  const display = o.buyerDisplayName?.trim();
  if (display) return display;
  if (o.buyerUsername) return `@${o.buyerUsername}`;
  return `TG #${o.buyerTelegramId ?? '—'}`;
  // END_BLOCK_RESOLVE_BUYER_PRIMARY_TITLE
}

function resolveBuyerSubline(o: OrderRow, primaryTitle: string): string {
  // START_BLOCK_RESOLVE_BUYER_SUBLINE
  const parts: string[] = [];
  if (o.buyerUsername && primaryTitle !== `@${o.buyerUsername}`) {
    parts.push(`@${o.buyerUsername}`);
  }
  if (o.buyerTelegramId) {
    parts.push(`#${o.buyerTelegramId}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'Покупатель';
  // END_BLOCK_RESOLVE_BUYER_SUBLINE
}

// START_CONTRACT: Orders
//   PURPOSE: Render the Orders management screen with buyer/recipient identity pills, filtering, search, retry, cancel, and Step-Up manual payment accreditation.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Calls /admin/orders/* endpoints
//   LINKS: M-ADMIN
// END_CONTRACT: Orders
export function Orders() {
  // START_BLOCK_ORDERS
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('ALL');
  const [provider, setProvider] = useState('ALL');
  const [q, setQ] = useState(() => searchParams.get('q') ?? '');
  const [detail, setDetail] = useState<string | null>(null);
  const [forceMatchTarget, setForceMatchTarget] = useState<OrderRow | null>(null);
  const { money } = useDisplayCurrency();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    const qParam = searchParams.get('q');
    if (qParam !== null) {
      setQ(qParam);
    }
  }, [searchParams]);

  const { data, isLoading } = useQuery({
    queryKey: ['orders', status, provider, q],
    queryFn: () =>
      api.get<OrderRow[]>(
        `/admin/orders?status=${status}&provider=${provider}&q=${encodeURIComponent(q)}&limit=100`,
      ),
    refetchInterval: 12000,
  });

  const act = useMutation({
    mutationFn: ({
      id,
      action,
    }: {
      id: string;
      action: 'cancel' | 'retry' | 'retry-delivery';
    }) => api.post<{ ok: boolean; reason?: string }>(`/admin/orders/${id}/${action}`),
    onSuccess: (r) => {
      toast(r.ok ? 'Действие выполнено' : (r.reason ?? 'Не удалось выполнить'));
      void qc.invalidateQueries({ queryKey: ['orders'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Ошибка запроса'),
  });

  const handleForceMatch = async ({
    reason,
    stepUpGrantToken,
  }: {
    reason: string;
    stepUpGrantToken?: string;
  }) => {
    if (!forceMatchTarget) return;
    await api.post(`/admin/orders/${forceMatchTarget.id}/force-match`, {
      reason,
      stepUpGrantToken,
    });
    toast(`Оплата заказа ${forceMatchTarget.shortId} засчитана вручную и отправлена в доставку`);
    void qc.invalidateQueries({ queryKey: ['orders'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const openBuyerInUsers = (o: OrderRow) => {
    const queryVal = String(o.buyerTelegramId ?? o.buyerUsername ?? '').trim();
    if (!queryVal && !o.userId) return;
    const params = new URLSearchParams();
    if (queryVal) params.set('q', queryVal);
    if (o.userId) params.set('userId', o.userId);
    navigate(`/users?${params.toString()}`);
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Заказы</h1>
          <div className="page-subtitle">
            Мониторинг покупок Stars, Telegram Premium и пополнений баланса
          </div>
        </div>
      </div>

      <div className="toolbar">
        <select
          aria-label="Фильтр статуса заказов"
          style={{ flex: '0 1 200px', minWidth: 0 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {ORDERS_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s === 'ALL' ? 'Все статусы' : s}
            </option>
          ))}
        </select>
        <select
          aria-label="Фильтр провайдера заказов"
          style={{ flex: '0 1 175px', minWidth: 0 }}
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
        >
          <option value="ALL">Все провайдеры</option>
          <option value="FRAGMENT">Fragment</option>
          <option value="PLAYWALLET">Play Wallet</option>
          <option value="MOOGOLD">MooGold</option>
          <option value="LOCAL_STOCK">Свой склад</option>
          <option value="GAMESDROP">GamesDrop</option>
          <option value="PIKASIM">PikaSim</option>
        </select>
        <input
          style={{ flex: '1 1 240px', minWidth: 0 }}
          placeholder="Поиск по ID заказа, товару, внешнему ref, @username или TG ID…"
          aria-label="Поиск заказов"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="muted">Загрузка списка заказов…</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table className="mobile-cards">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Статус</th>
                  <th>Товар</th>
                  <th>Получатель / Покупатель</th>
                  <th>К оплате</th>
                  <th>Создан</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((o) => {
                  const buyerTitle = resolveBuyerPrimaryTitle(o);
                  const buyerSub = resolveBuyerSubline(o, buyerTitle);
                  const canOpenBuyer = Boolean(o.buyerTelegramId ?? o.buyerUsername);
                  const giftSenderText = o.buyerUsername
                    ? `@${o.buyerUsername}`
                    : (o.buyerDisplayName?.trim() || `#${o.buyerTelegramId ?? '—'}`);

                  return (
                    <tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => setDetail(o.id)}>
                      <td className="mono mobile-card-primary" data-label="" style={{ fontWeight: 700 }}>
                        {o.shortId}
                      </td>
                      <td data-label="Статус">
                        <span className={'chip ' + o.status}>{o.status}</span>
                      </td>
                      <td data-label="Товар">{productLabel(o)}</td>
                      <td data-label="Получатель / Покупатель" onClick={(e) => e.stopPropagation()}>
                        {o.recipientType === 'self' ? (
                          <button
                            type="button"
                            onClick={() => openBuyerInUsers(o)}
                            disabled={!canOpenBuyer}
                            title={
                              canOpenBuyer
                                ? `Открыть карточку покупателя ${buyerTitle}`
                                : buyerTitle
                            }
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 9,
                              padding: '3px 7px 3px 3px',
                              borderRadius: 9,
                              background: 'transparent',
                              border: '1px solid transparent',
                              color: 'inherit',
                              textAlign: 'left',
                              cursor: canOpenBuyer ? 'pointer' : 'default',
                            }}
                          >
                            <UserAvatar
                              photoUrl={o.buyerPhotoUrl}
                              name={buyerTitle}
                              size={26}
                            />
                            <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <span style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.2 }}>
                                {buyerTitle}
                              </span>
                              <span
                                className="mono muted"
                                style={{ fontSize: 11, lineHeight: 1.2 }}
                              >
                                {buyerSub}
                              </span>
                            </span>
                          </button>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span className="mono" style={{ fontWeight: 600 }}>
                                @{o.recipientUsername ?? '—'}
                              </span>
                              <span
                                className="chip info"
                                style={{ padding: '2px 7px', fontSize: 10.5 }}
                              >
                                Подарок
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => openBuyerInUsers(o)}
                              disabled={!canOpenBuyer}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                textAlign: 'left',
                                fontSize: 11.5,
                                color: canOpenBuyer ? 'var(--accent)' : 'var(--ink-faint)',
                                cursor: canOpenBuyer ? 'pointer' : 'default',
                              }}
                            >
                              от <span className="mono">{giftSenderText}</span>
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="mono" data-label="К оплате">
                        {o.paymentRail === 'gram' && (o.gramAppliedMilli ?? 0) > 0 ? (
                          <>
                            {((o.gramAppliedMilli ?? 0) / 1000).toFixed(2)} GRAM
                            <div className="muted" style={{ fontSize: 11 }}>
                              экв. {rub(o.retailTotalKopecks ?? 0)} ₽
                            </div>
                          </>
                        ) : (
                          <>
                            {money(o.amountKopecks)}
                            <div className="muted" style={{ fontSize: 11 }}>
                              {rub(o.amountKopecks)} ₽
                              {o.balanceAppliedKopecks > 0 &&
                                ` (+${rub(o.balanceAppliedKopecks)} ₽ с баланса)`}
                            </div>
                          </>
                        )}
                      </td>
                      <td className="muted mono" data-label="Создан">
                        {dt(o.createdAt)}
                      </td>
                      <td
                        className="mobile-card-actions"
                        data-label=""
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="row" style={{ gap: 6 }}>
                          {(o.status === 'AWAITING_PAYMENT' ||
                            o.status === 'MATCHING' ||
                            o.status === 'EXPIRED') && (
                            <button
                              type="button"
                              className="btn sm primary"
                              onClick={() => setForceMatchTarget(o)}
                              title="Вручную подтвердить оплату заказа и отправить заказ в автоматическую доставку (требуется Step-Up код)"
                            >
                              <AdminIcon name="bolt" size={13} />
                              <span>Засчитать оплату</span>
                            </button>
                          )}
                          {(o.status === 'AWAITING_PAYMENT' || o.status === 'MATCHING') && (
                            <button
                              type="button"
                              className="btn danger sm"
                              onClick={() => act.mutate({ id: o.id, action: 'cancel' })}
                            >
                              Отменить
                            </button>
                          )}
                          {(o.status === 'DELIVERY_FAILED' ||
                            o.status === 'DELIVERING_ASYNC' ||
                            (o.status === 'MATCHED' && Boolean(o.nextDeliveryAttemptAt))) && (
                            <button
                              type="button"
                              className="btn sm"
                              onClick={() => act.mutate({ id: o.id, action: 'retry-delivery' })}
                            >
                              Повторить сейчас
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn sm ghost"
                            onClick={() => setDetail(o.id)}
                          >
                            Детали
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!data?.length && (
                  <tr>
                    <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 28 }}>
                      Заказы по заданному фильтру не найдены
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detail && (
        <OrderDetail
          id={detail}
          onClose={() => setDetail(null)}
          onOpenUser={(queryVal) => {
            setDetail(null);
            navigate(`/users?q=${encodeURIComponent(queryVal)}`);
          }}
          onForceMatch={(o) => {
            setDetail(null);
            setForceMatchTarget(o);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(forceMatchTarget)}
        title={`Ручное зачисление оплаты заказа ${forceMatchTarget?.shortId ?? ''}`}
        description={
          forceMatchTarget
            ? `Используйте это действие, если клиент перевёл деньги по СБП, но автоматический парсер Т-Банка не сопоставил платёж (например, не совпали копейки или банк задержал пуш). Заказ на ${productText(forceMatchTarget)} (${rub(forceMatchTarget.amountKopecks)} ₽) получит статус MATCHED и будет немедленно отправлен на автоматическую выдачу (${formatProviderBadgeLabel(forceMatchTarget.externalProvider)}).`
            : ''
        }
        confirmLabel="Засчитать оплату и выдать"
        danger
        stepUpAction="order.force_match"
        onCancel={() => setForceMatchTarget(null)}
        onConfirm={handleForceMatch}
      />
    </>
  );
  // END_BLOCK_ORDERS
}

interface OrderFull extends OrderRow {
  fragmentRef: string | null;
  initials: string | null;
  paidMatchedAt: string | null;
  fulfillmentInput?: {
    steamLogin?: string;
    playerId?: string;
    serverId?: string;
    playerNickname?: string;
    customAmountRub?: number;
  } | null;
  deliverySummary?: {
    kind: string;
    externalStatus?: string | null;
    iccid?: string | null;
    countryIso?: string | null;
    hasSealedSecret: boolean;
  } | null;
  buyer: {
    id: string;
    telegramId: number;
    username: string | null;
    firstName: string | null;
    customName?: string | null;
    displayName?: string | null;
    photoUrl?: string | null;
  } | null;
  payment: { id: string; amountKopecks: number; sender: string; observedAt: string } | null;
}

function OrderDetail({
  id,
  onClose,
  onOpenUser,
  onForceMatch,
}: {
  id: string;
  onClose: () => void;
  onOpenUser: (queryVal: string) => void;
  onForceMatch: (order: OrderRow) => void;
}) {
  // START_BLOCK_ORDER_DETAIL
  const { data } = useQuery({
    queryKey: ['order', id],
    queryFn: () => api.get<OrderFull>(`/admin/orders/${id}`),
  });

  const buyerName = data
    ? data.buyer?.displayName?.trim() ||
      data.buyer?.customName?.trim() ||
      data.buyer?.firstName?.trim() ||
      data.buyerDisplayName?.trim() ||
      (data.buyer?.username
        ? `@${data.buyer.username}`
        : data.buyerUsername
          ? `@${data.buyerUsername}`
          : `TG #${data.buyer?.telegramId ?? data.buyerTelegramId ?? '—'}`)
    : '—';

  const buyerUsername = data?.buyer?.username ?? data?.buyerUsername ?? null;
  const buyerTgId = data?.buyer?.telegramId ?? data?.buyerTelegramId ?? null;
  const buyerPhoto = data?.buyer?.photoUrl ?? data?.buyerPhotoUrl ?? null;
  const buyerSearchQuery = String(buyerTgId ?? buyerUsername ?? '').trim();

  const modalBody = (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card"
        style={{ maxWidth: 'min(680px, 100%)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {!data ? (
          <div className="muted">Загрузка карточки заказа…</div>
        ) : (
          <>
            <div className="row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0 }}>
                Заказ <span className="mono">{data.shortId}</span>
              </h2>
              <span className={'chip ' + data.status}>{data.status}</span>
            </div>
            <div className="table-wrap" style={{ marginTop: 14 }}>
              <table>
                <tbody>
                  <Row k="UUID" v={<span className="mono">{data.id}</span>} />
                  <Row k="Товар" v={productLabel(data)} />
                  <Row
                    k="Получатель"
                    v={
                      data.recipientType === 'self' ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <UserAvatar photoUrl={buyerPhoto} name={buyerName} size={22} />
                          <strong>{buyerName}</strong>
                          {buyerUsername && buyerName !== `@${buyerUsername}` && (
                            <span className="mono muted">@{buyerUsername}</span>
                          )}
                          <span className="muted" style={{ fontSize: 12 }}>
                            (покупка на свой аккаунт)
                          </span>
                        </span>
                      ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <strong className="mono">@{data.recipientUsername ?? '—'}</strong>
                          <span className="chip info" style={{ padding: '2px 7px', fontSize: 10.5 }}>
                            Подарок
                          </span>
                          <span className="muted" style={{ fontSize: 12 }}>
                            от {buyerUsername ? `@${buyerUsername}` : buyerName}
                          </span>
                        </span>
                      )
                    }
                  />
                  <Row
                    k="Сумма к оплате"
                    v={
                      data.paymentRail === 'gram' && (data.gramAppliedMilli ?? 0) > 0 ? (
                        <span className="mono">
                          {((data.gramAppliedMilli ?? 0) / 1000).toFixed(2)} GRAM (экв.{' '}
                          {rub(data.retailTotalKopecks ?? 0)} ₽)
                        </span>
                      ) : (
                        <span className="mono">{rub(data.amountKopecks)} ₽</span>
                      )
                    }
                  />
                  <Row
                    k="Списано с баланса"
                    v={<span className="mono">{rub(data.balanceAppliedKopecks)} ₽</span>}
                  />
                  {data.fulfillmentInput && (
                    <Row
                      k="Реквизиты получателя"
                      v={
                        <span className="mono">
                          {[
                            data.fulfillmentInput.steamLogin
                              ? `Steam: ${data.fulfillmentInput.steamLogin}`
                              : null,
                            data.fulfillmentInput.playerId
                              ? `UID: ${data.fulfillmentInput.playerId}`
                              : null,
                            data.fulfillmentInput.serverId
                              ? `Server: ${data.fulfillmentInput.serverId}`
                              : null,
                            data.fulfillmentInput.playerNickname
                              ? `Ник: ${data.fulfillmentInput.playerNickname}`
                              : null,
                            data.fulfillmentInput.customAmountRub
                              ? `Сумма: ${data.fulfillmentInput.customAmountRub} ₽`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </span>
                      }
                    />
                  )}
                  {data.deliverySummary && (
                    <Row
                      k="Статус выдачи"
                      v={
                        <span className="mono">
                          {[
                            `Тип: ${data.deliverySummary.kind}`,
                            data.deliverySummary.iccid ? `ICCID: ${data.deliverySummary.iccid}` : null,
                            data.deliverySummary.countryIso
                              ? `Страна: ${data.deliverySummary.countryIso}`
                              : null,
                            data.deliverySummary.hasSealedSecret
                              ? 'Код/LPA зашифрован (AES-256-GCM)'
                              : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      }
                    />
                  )}
                  <Row
                    k="Покупатель"
                    v={
                      data.buyer || buyerTgId || buyerUsername ? (
                        <button
                          type="button"
                          disabled={!buyerSearchQuery}
                          onClick={() => buyerSearchQuery && onOpenUser(buyerSearchQuery)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            flexWrap: 'wrap',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: buyerSearchQuery ? 'var(--accent)' : 'inherit',
                            cursor: buyerSearchQuery ? 'pointer' : 'default',
                            textAlign: 'left',
                          }}
                        >
                          <UserAvatar photoUrl={buyerPhoto} name={buyerName} size={22} />
                          <span style={{ fontWeight: 600 }}>{buyerName}</span>
                          <span className="mono muted">
                            {buyerUsername ? `@${buyerUsername} · ` : ''}
                            {buyerTgId ? `#${buyerTgId}` : ''}
                          </span>
                        </button>
                      ) : (
                        '—'
                      )
                    }
                  />
                  <Row k="Инициалы плательщика" v={data.initials ?? '—'} />
                  <Row k="Создан" v={<span className="mono">{dt(data.createdAt)}</span>} />
                  <Row k="Оплачен" v={<span className="mono">{dt(data.paidMatchedAt)}</span>} />
                  <Row k="Доставлен" v={<span className="mono">{dt(data.deliveredAt)}</span>} />
                  <Row
                    k="Провайдер выдачи"
                    v={
                      <span className="mono">
                        {formatProviderBadgeLabel(data.externalProvider)}
                        {data.externalOrderRef ? ` · ref: ${data.externalOrderRef}` : ''}
                      </span>
                    }
                  />
                  <Row k="Fragment Ref" v={<span className="mono">{data.fragmentRef ?? '—'}</span>} />
                  {data.failureReason && (
                    <Row k="Причина сбоя" v={<span className="error">{data.failureReason}</span>} />
                  )}
                  {data.payment && (
                    <Row
                      k="Входящий платёж"
                      v={
                        <span className="mono">
                          {rub(data.payment.amountKopecks)} ₽ · {data.payment.sender} ·{' '}
                          {dt(data.payment.observedAt)}
                        </span>
                      }
                    />
                  )}
                </tbody>
              </table>
            </div>
            <div className="row" style={{ marginTop: 18, justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              {data.status === 'AWAITING_PAYMENT' ||
              data.status === 'MATCHING' ||
              data.status === 'EXPIRED' ? (
                <button
                  type="button"
                  className="btn primary"
                  title="Вручную подтвердить оплату заказа и отправить заказ в автоматическую доставку (требуется Step-Up код)"
                  onClick={() => onForceMatch(data)}
                >
                  <AdminIcon name="bolt" size={13} />
                  <span>Засчитать оплату</span>
                </button>
              ) : (
                <span />
              )}
              <button type="button" className="btn ghost" onClick={onClose}>
                Закрыть
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(modalBody, document.body);
  }
  return modalBody;
  // END_BLOCK_ORDER_DETAIL
}

function Row({ k, v }: { k: string; v: ReactNode }) {
  // START_BLOCK_ROW
  return (
    <tr>
      <td className="muted" style={{ width: '36%', minWidth: 110, maxWidth: 170 }}>
        {k}
      </td>
      <td style={{ overflowWrap: 'anywhere' }}>{v}</td>
    </tr>
  );
  // END_BLOCK_ROW
}
